import { shallow } from "zustand/shallow"
import { useStoreWithEqualityFn } from "zustand/traditional"
import { type StoreApi } from "zustand/vanilla"

import type {
  GuildWarRosterRoleSlot,
  GuildWarRosterSlotTarget,
  GuildWarRosterSquadKey,
  GuildWarRosterTeamKey,
  GuildWarSquadTeamRoster,
} from "@/core/guild-war/types"
import type {
  GuildWarRosterBoard,
  GuildWarRosterMemberPathSnapshot,
  GuildWarRosterSubteam,
  GuildWarTeamRoster,
} from "@/lib/guild-war-wire-types"

import {
  cloneGuildWarRosterBoard,
  cloneGuildWarTeamRoster,
  createEmptyGuildWarRosterBoard,
  createEmptyGuildWarTeamRoster,
  getGuildWarRosterTeamKeys,
  hasGuildWarViceCommanderSlot,
  isDualGuildWarRosterBoard,
  isGuildWarFlatTeamRoster,
} from "@/core/guild-war/roster-board"
import {
  getGuildWarAssignedMemberIds,
  getGuildWarRosterBoardValidationSummary,
  getGuildWarTeamRosterMembers,
  getGuildWarTeamValidationSummary,
  type GuildWarRosterBoardValidationSummary,
  type GuildWarRosterHealthStatus,
  type GuildWarRosterTeamValidationSummary,
} from "@/core/guild-war/roster-validation"
import { coerceStringRecord } from "@/lib/utils/coerce-utils"

export type TeamKey = GuildWarRosterTeamKey
export type SquadKey = GuildWarRosterSquadKey
export type TeamRoleSlot = GuildWarRosterRoleSlot
export type TeamBoardSlotTarget = GuildWarRosterSlotTarget
export type TeamSubteam = GuildWarRosterSubteam
export type TeamRoster = GuildWarTeamRoster
export type TeamBoardState = GuildWarRosterBoard

export type TeamHealthStatus = GuildWarRosterHealthStatus

export type TeamValidationSummary = GuildWarRosterTeamValidationSummary

export type BoardValidationSummary = GuildWarRosterBoardValidationSummary

type LegacyTeamBoardState = {
  mobility?: string[]
  attack?: string[]
  defense?: string[]
}

type LegacyStructuredTeamRoster = {
  commander?: string | null
  subcommanders?: Array<string | null>
  viceCommander?: string | null
  subteams?: Array<{ key?: SquadKey; members?: Array<string | null> }>
}

type LegacyMemberPathSnapshot = {
  primaryPathCode?: string | null
  secondaryPathCode?: string | null
}

export const createEmptyTeamRoster = createEmptyGuildWarTeamRoster
export const emptyBoard: TeamBoardState = createEmptyGuildWarRosterBoard()
const cloneTeamRoster = cloneGuildWarTeamRoster
const cloneBoard = cloneGuildWarRosterBoard

const removeFromTeamRoster = (
  team: TeamRoster,
  memberId: string,
): TeamRoster => {
  const next = cloneTeamRoster(team)
  next.commander = next.commander === memberId ? null : next.commander
  if (hasGuildWarViceCommanderSlot(next)) {
    next.viceCommander =
      next.viceCommander === memberId ? null : next.viceCommander
  }

  if (isGuildWarFlatTeamRoster(next)) {
    next.members = next.members.map((value) =>
      value === memberId ? null : value,
    )
  } else {
    next.subteams = next.subteams.map((subteam) => ({
      key: subteam.key,
      members: subteam.members.map((value) =>
        value === memberId ? null : value,
      ),
    })) as GuildWarSquadTeamRoster["subteams"]
  }

  return next
}

export const removeFromAllSlots = (
  board: TeamBoardState,
  memberId: string,
): TeamBoardState => {
  const next = cloneBoard(board)
  for (const team of getGuildWarRosterTeamKeys(board)) {
    Object.assign(next, {
      [team]: removeFromTeamRoster(board[team]!, memberId),
    })
  }
  next.commanders = board.commanders.map((value) =>
    value === memberId ? null : value,
  ) as TeamBoardState["commanders"]
  next.halftimePerformers = board.halftimePerformers.map((value) =>
    value === memberId ? null : value,
  ) as TeamBoardState["halftimePerformers"]
  return next
}

const assignMemberToFirstOpenTeamSlot = (
  team: GuildWarSquadTeamRoster,
  memberId: string,
): GuildWarSquadTeamRoster => {
  const next = cloneTeamRoster(team) as GuildWarSquadTeamRoster

  if (!next.commander) {
    next.commander = memberId
    return next
  }
  if (!next.viceCommander) {
    next.viceCommander = memberId
    return next
  }
  for (const subteam of next.subteams) {
    const slotIndex = subteam.members.findIndex((value) => value === null)
    if (slotIndex >= 0) {
      subteam.members[slotIndex] = memberId
      return next
    }
  }
  return next
}

export const getTeamRosterMembers = getGuildWarTeamRosterMembers
export const getAssignedMemberIds = getGuildWarAssignedMemberIds
export const getTeamValidationSummary = getGuildWarTeamValidationSummary
export const getBoardValidationSummary = getGuildWarRosterBoardValidationSummary

const createEmptyHistoricalTeamRoster = (): GuildWarSquadTeamRoster => ({
  commander: null,
  viceCommander: null,
  subteams: [
    { key: "alpha", members: [null, null, null, null] },
    { key: "beta", members: [null, null, null, null] },
  ],
})

const fillTeamFromLegacyMembers = (
  memberIds: string[] = [],
): GuildWarSquadTeamRoster => {
  let team = createEmptyHistoricalTeamRoster()
  for (const memberId of memberIds.slice(0, 10)) {
    team = assignMemberToFirstOpenTeamSlot(team, memberId)
  }
  return team
}

const coerceTeamRoster = (value: unknown, teamKey: TeamKey): TeamRoster => {
  if (!value || typeof value !== "object") {
    return createEmptyTeamRoster(teamKey)
  }

  const maybeTeam = value as LegacyStructuredTeamRoster & {
    members?: Array<string | null>
  }
  if (Array.isArray(maybeTeam.members)) {
    return "viceCommander" in maybeTeam
      ? {
          commander: maybeTeam.commander ?? null,
          viceCommander: maybeTeam.viceCommander ?? null,
          members: [...maybeTeam.members],
        }
      : {
          commander: maybeTeam.commander ?? null,
          members: [...maybeTeam.members],
        }
  }

  const subteams = Array.isArray(maybeTeam.subteams) ? maybeTeam.subteams : []
  return {
    commander: maybeTeam.commander ?? null,
    viceCommander:
      maybeTeam.viceCommander ?? maybeTeam.subcommanders?.[0] ?? null,
    subteams: [0, 1].map((index) => ({
      key: (subteams[index]?.key ??
        (index === 0 ? "alpha" : "beta")) as SquadKey,
      members: [...(subteams[index]?.members ?? [])],
    })) as GuildWarSquadTeamRoster["subteams"],
  }
}

export const coerceTeamBoardState = (value: unknown): TeamBoardState => {
  if (!value || typeof value !== "object") {
    return cloneBoard(emptyBoard)
  }

  if (isDualGuildWarRosterBoard(value)) {
    return cloneBoard(value)
  }

  const maybeBoard = value as Partial<TeamBoardState> & LegacyTeamBoardState

  const hasStructuredRoster = (["mobility", "attack", "defense"] as const).some(
    (team) =>
      maybeBoard[team] &&
      typeof maybeBoard[team] === "object" &&
      ("subteams" in maybeBoard[team] || "members" in maybeBoard[team]),
  )
  if (hasStructuredRoster) {
    const memberPaths =
      maybeBoard.memberPaths && typeof maybeBoard.memberPaths === "object"
        ? (Object.fromEntries(
            Object.entries(maybeBoard.memberPaths as Record<string, unknown>)
              .filter(
                ([, snapshot]) => snapshot && typeof snapshot === "object",
              )
              .map(([memberId, snapshot]) => {
                const pathSnapshot = snapshot as LegacyMemberPathSnapshot
                return [
                  memberId,
                  {
                    primaryPathCode: pathSnapshot.primaryPathCode ?? null,
                    secondaryPathCode: pathSnapshot.secondaryPathCode ?? null,
                  },
                ]
              }),
          ) as Record<string, GuildWarRosterMemberPathSnapshot>)
        : {}

    return {
      mobility: coerceTeamRoster(maybeBoard.mobility, "mobility"),
      attack: coerceTeamRoster(maybeBoard.attack, "attack"),
      defense: coerceTeamRoster(maybeBoard.defense, "defense"),
      memberPaths,
      memberDisplayNames: coerceStringRecord(maybeBoard.memberDisplayNames),
      commanders: Array.isArray(maybeBoard.commanders)
        ? [
            maybeBoard.commanders[0] ?? null,
            maybeBoard.commanders[1] ?? null,
            maybeBoard.commanders[2] ?? null,
            maybeBoard.commanders[3] ?? null,
            maybeBoard.commanders[4] ?? null,
          ]
        : [null, null, null, null, null],
      halftimePerformers: Array.isArray(maybeBoard.halftimePerformers)
        ? [
            maybeBoard.halftimePerformers[0] ?? null,
            maybeBoard.halftimePerformers[1] ?? null,
            maybeBoard.halftimePerformers[2] ?? null,
          ]
        : [null, null, null],
    }
  }

  return {
    mobility: fillTeamFromLegacyMembers(maybeBoard.mobility),
    attack: fillTeamFromLegacyMembers(maybeBoard.attack),
    defense: fillTeamFromLegacyMembers(maybeBoard.defense),
    memberPaths: {},
    memberDisplayNames: {},
    commanders: [null, null, null, null, null],
    halftimePerformers: [null, null, null],
  }
}

/**
 * Subscribes to assigned member IDs (memoized selector)
 * Useful for filtering available candidates
 */
export const useAssignedMemberIds = <S extends { board: TeamBoardState }>(
  store: StoreApi<S>,
) =>
  useStoreWithEqualityFn(store, (s) => getAssignedMemberIds(s.board), shallow)

/**
 * Gets the member ID assigned to a specific slot target
 * Useful for determining which member is currently selected
 */
export function getMemberIdForTarget(
  board: TeamBoardState,
  target: TeamBoardSlotTarget,
): string | null {
  if (target.kind === "commander-overlay") {
    return board.commanders[target.index] ?? null
  }

  if (target.kind === "halftime") {
    return board.halftimePerformers[target.index] ?? null
  }

  if (
    (target.kind === "team-role" ||
      target.kind === "team-member" ||
      target.kind === "subteam-member") &&
    !Object.hasOwn(board, target.team)
  ) {
    return null
  }

  if (target.kind === "team-role") {
    const team = board[target.team]!
    return target.role === "viceCommander" &&
      !hasGuildWarViceCommanderSlot(team)
      ? null
      : (team[target.role] ?? null)
  }

  const team = board[target.team]!
  if (target.kind === "team-member") {
    return isGuildWarFlatTeamRoster(team) &&
      Number.isInteger(target.index) &&
      target.index >= 0
      ? (team.members[target.index] ?? null)
      : null
  }

  if (!Number.isInteger(target.index) || target.index < 0) return null
  const subteamIndex = target.squad === "alpha" ? 0 : 1
  return isGuildWarFlatTeamRoster(team)
    ? null
    : (team.subteams[subteamIndex]?.members[target.index] ?? null)
}
